import type { LucideIcon } from 'lucide-react'
import { ArrowRight } from 'lucide-react'

// ---------------------------------------------------------------------------
// "Start from…" action card — icon + title + blurb + call-to-action arrow.
// Shared by the Builder hub (/builder) and the Workbench landing page so the
// two entry points into the product look and behave identically.
// ---------------------------------------------------------------------------

export interface StartCardProps {
  icon: LucideIcon
  title: string
  blurb: string
  cta: string
  /** Muted outline tag pinned top-right (e.g. "Advanced"). */
  badge?: string
  /** Filled accent pill pinned top-right (e.g. "2 shared"). Ignored when `badge` is set. */
  accentBadge?: string
  disabled?: boolean
  /** Compact variant — smaller padding, no min-height. Used in dense grids. */
  compact?: boolean
  onClick?: () => void
}

export default function StartCard({
  icon: Icon,
  title,
  blurb,
  cta,
  badge,
  accentBadge,
  disabled,
  compact,
  onClick,
}: StartCardProps) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: compact ? 8 : 12,
        padding: compact ? '1rem' : '1.25rem',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'all 160ms ease',
        position: 'relative',
        color: 'var(--color-text)',
        minHeight: compact ? undefined : 180,
      }}
      onMouseEnter={(e) => {
        if (disabled) return
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.borderColor = 'var(--color-accent)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.borderColor = 'var(--color-border)'
      }}
    >
      {badge && (
        <span
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: 999,
            background: 'var(--color-bg)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          {badge}
        </span>
      )}
      {accentBadge && !badge && (
        <span
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.03em',
            padding: '2px 8px',
            borderRadius: 999,
            background: 'var(--color-accent)',
            color: '#fff',
          }}
        >
          {accentBadge}
        </span>
      )}
      <div
        style={{
          width: compact ? 30 : 36,
          height: compact ? 30 : 36,
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-accent)',
          flexShrink: 0,
        }}
      >
        <Icon size={compact ? 16 : 18} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: '1rem', fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {blurb}
        </div>
      </div>
      <div
        style={{
          marginTop: 'auto',
          paddingTop: compact ? 4 : 0,
          fontSize: '0.875rem',
          color: disabled ? 'var(--color-text-secondary)' : 'var(--color-accent)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {cta}
        {!disabled && <ArrowRight size={14} />}
      </div>
    </button>
  )
}
